import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { MultiCartService } from '@spartacus/cart/base/core';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { BaseSiteService, ClientTokenService, TranslationService, UserIdService } from '@spartacus/core';
import * as moment from 'moment-timezone';
import { BehaviorSubject, Observable, Subject, combineLatest, of, throwError } from 'rxjs';
import { catchError, filter, map, retry, switchMap, take } from 'rxjs/operators';
import { ORDER_HISTORY_BASESITE_CONFIG_KEYS } from 'src/app/model/order-history.model';
import { environment } from 'src/environments/environment';
import { BaseSiteConfigService } from '../base-site/base-site-config.service';
import { ComponentService } from '../component.service';
import { ShimanoCountryService } from '../country/shimano-country.service';
import { DownloadOptions, ShimanoFileDownloadService } from '../file/shimano-file-download.service';

export interface GetOrdersPayload {
  currentPage?: number;
  dir?: string;
  fields?: string;
  filterSearchData?: string;
  filterSearchBy?: string;
  fromDate?: string;
  orderStatus?: any;
  orderTypes?: any;
  pageSize?: number;
  sort?: string;
  toDate?: string;
}

export interface OrderHistoryPaginationModel {
  currentPage?: number;
  pageSize?: number;
  sort?: string;
  totalPages?: number;
  totalResults?: number;
  pageNumber?: number;
}

export interface GetInvoicesPayload {
  currentPage?: number;
  dir?: string;
  fields?: string;
  filterSearchData?: string;
  filterSearchBy?: string;
  fromDate?: string;
  toDate?: string;
  orderStatus?: any;
  orderTypes?: any;
  pageSize?: number;
  sort?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OrderHistoryService {
  baseSite$: Observable<string> = this.baseService.getActive();
  activeCart$: Observable<string> = this.cartService.getActiveCartId();
  userCurrent$: Observable<string> = this.auth.getUserId();
  occUrl = this.meta.getTag('name=occ-backend-base-url');
  url = `${this.occUrl?.content}${environment.prefix}${this.baseSiteId}`;
  headers = new HttpHeaders().set('Content-Type', 'application/json');
  sortLabels?: Observable<{
    orderdateasc: string;
    orderdatedesc: string;
    ordernumberasc: string;
    ordernumberdesc: string;
    ponumberasc: string;
    ponumberdesc: string;
    ordertype: string;
  }>;
  private filterSearch = new Subject<any>();
  private filterSearchBy = new Subject<any>();
  private setOrderStatus = new Subject<any>();
  private setOrderType = new Subject<any>();
  private removeOrderStatus = new Subject<any>();
  private statusOpen = new Subject<any>();
  private clearFilters = new Subject<any>();
  private selectedFilter = new Subject<any>();
  private fromDate = new Subject<any>();
  private toDate = new Subject<any>();
  private totalOrders = new Subject<any>();
  private triggerOrders = new Subject<[status: boolean, isInOrderHistoryPage: boolean]>();
  sortChange$ = new Subject<string>();
  getOrderHistory$ = new BehaviorSubject<[orderView: string, onScroll: boolean, onInit: boolean]>([null, false, false]);
  getInvoiceHistory$ = new BehaviorSubject<[onScroll: boolean]>([false]);
  filterSearch$ = this.filterSearch.asObservable();
  filterSearchBy$ = this.filterSearchBy.asObservable();
  setOrderStatus$ = this.setOrderStatus.asObservable();
  setOrderType$ = this.setOrderType.asObservable();
  removeOrderStatus$ = this.removeOrderStatus.asObservable();
  statusOpen$ = this.statusOpen.asObservable();
  clearFilters$ = this.clearFilters.asObservable();
  selectedFilter$ = this.selectedFilter.asObservable();
  fromDate$ = this.fromDate.asObservable();
  toDate$ = this.toDate.asObservable();
  totalOrders$ = this.totalOrders.asObservable();
  triggerOrders$ = this.triggerOrders.asObservable();
  constructor(
    protected cartService: ActiveCartFacade,
    protected http: HttpClient,
    protected auth: UserIdService,
    protected clientToken: ClientTokenService,
    protected baseService: BaseSiteService,
    protected translation: TranslationService,
    protected countryService: ShimanoCountryService,
    protected shimanoFileDownloadService: ShimanoFileDownloadService,
    protected baseSiteConfigService: BaseSiteConfigService,
    private meta: Meta,
    private multiCartService: MultiCartService,
    private componentService: ComponentService
  ) {}

  setFilterSearch(filterData: string) {
    this.filterSearch.next(filterData);
  }

  setFilterSearchBy(filterData: string) {
    this.filterSearchBy.next(filterData);
  }

  setOrderStatuses(status: number) {
    this.setOrderStatus.next(status);
  }

  setOrderTypes(orderTypes: any[]) {
    const selectedOrderTypes = orderTypes.filter((orderType) => orderType.selected).map((orderType) => orderType.type);
    this.setOrderType.next(selectedOrderTypes);
  }

  removeOrderStatuses(status: number) {
    this.removeOrderStatus.next(status);
  }

  setFromDate(date: string) {
    this.fromDate.next(date);
  }
  setToDate(date: string) {
    this.toDate.next(date);
  }
  setTotalOrders(count: number) {
    this.totalOrders.next(count);
  }
  setClearFilters(clearTriggered: boolean) {
    this.clearFilters.next(clearTriggered);
  }
  setSelectedFilter(status: number) {
    this.selectedFilter.next(status);
  }

  refreshOrders(status: boolean, isInOrderHistoryPage: boolean) {
    this.triggerOrders.next([status, isInOrderHistoryPage]);
  }

  get baseSiteId(): string {
    let baseSiteId: any;
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  get userCurrent(): string {
    let userCurrent: any;
    this.userCurrent$.pipe(take(1)).subscribe((userId) => (userCurrent = userId));
    return userCurrent;
  }

  get tokenId(): string {
    let tokenId: any;
    this.clientToken
      .getClientToken()
      .pipe(take(1))
      .subscribe((tokenIdData) => (tokenId = tokenIdData.access_token));
    return tokenId;
  }

  getOrders(payload: GetOrdersPayload): Observable<any> {
    const body = {
      currentPage: payload.currentPage,
      dir: payload.dir,
      fromDate: payload.fromDate,
      pageSize: payload.pageSize,
      search: payload.filterSearchData,
      searchBy: payload.filterSearchBy,
      sort: payload.sort,
      status: payload.orderStatus,
      orderType: payload.orderTypes,
      toDate: payload.toDate,
    };

    const params = new HttpParams().set('fields', payload.fields);

    return this.http
      .post(`${this.url}/users/${this.userCurrent}/getOrders`, JSON.stringify(body), {
        params,
        headers: this.headers,
      })
      .pipe(map((data) => data));
  }

  getBackOrders(payload: GetOrdersPayload): Observable<any> {
    const body = {
      currentPage: payload.currentPage,
      dir: payload.dir,
      fromDate: payload.fromDate,
      pageSize: payload.pageSize,
      search: payload.filterSearchData,
      searchBy: payload.filterSearchBy,
      sort: payload.sort,
      status: payload.orderStatus,
      orderType: payload.orderTypes,
      toDate: payload.toDate,
    };

    const params = new HttpParams().set('fields', payload.fields);

    return this.http
      .post(`${this.url}/users/${this.userCurrent}/articles/backorder`, JSON.stringify(body), {
        params,
        headers: this.headers,
      })
      .pipe(map((data) => data));
  }

  getOrdersBackOrders(payload: GetOrdersPayload, type: string): Observable<any> {
    const body = {
      currentPage: payload.currentPage,
      dir: payload.dir,
      fromDate: payload.fromDate,
      pageSize: payload.pageSize,
      search: payload.filterSearchData,
      searchBy: payload.filterSearchData === '' ? '' : payload.filterSearchBy,
      sort: payload.sort,
      status: payload.orderStatus,
      orderType: payload.orderTypes,
      toDate: payload.toDate,
    };

    const params = new HttpParams().set('fields', payload.fields);
    const urlType = type === 'orders' ? '/getOrders' : '/articles/backorder';

    this.setOrderHistoryFilterObservable({
      type: type,
      orderStatus: payload.orderStatus,
      orderType: payload.orderTypes,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
    });

    return this.http
      .post(`${this.url}/users/${this.userCurrent}${urlType}`, JSON.stringify(body), {
        params,
        headers: this.headers,
      })
      .pipe(map((data) => data));
  }

  setOrderHistoryFilterObservable(object: any) {
    // used for retaining selected filters on mobile, tablet, desktop view
    this.componentService.orderHistoryFilter$.next({
      ...this.componentService.orderHistoryFilter$.value,
      ...object,
    });
  }

  clearOrderHistoryFilterObservable() {
    this.componentService.orderHistoryFilter$.next(null);
  }

  getSortLabels(): Observable<{
    orderdateasc: string;
    orderdatedesc: string;
    deliverydateasc: string;
    deliverydatedesc: string;
    ordernumberasc: string;
    ordernumberdesc: string;
    ponumberasc: string;
    ponumberdesc: string;
    ordertype: string;
    itemnumberasc: string;
    itemnumberdesc: string;
    holdcodeasc: string;
    holdcodedesc: string;
    daterequestasc: string;
    daterequestdesc: string;
  }> {
    return combineLatest([
      this.translation.translate('orders.sortLabels.orderDateAsc'),
      this.translation.translate('orders.sortLabels.orderDateDesc'),
      this.translation.translate('orders.sortLabels.deliveryDateAsc'),
      this.translation.translate('orders.sortLabels.deliveryDateDesc'),
      this.translation.translate('orders.sortLabels.orderNumberAsc'),
      this.translation.translate('orders.sortLabels.orderNumberDesc'),
      this.translation.translate('orders.sortLabels.poNumberAsc'),
      this.translation.translate('orders.sortLabels.poNumberDesc'),
      this.translation.translate('orders.sortLabels.orderType'),
      this.translation.translate('orders.sortLabels.itemNumberAsc'),
      this.translation.translate('orders.sortLabels.itemNumberDesc'),
      this.translation.translate('orders.sortLabels.holdCodeAsc'),
      this.translation.translate('orders.sortLabels.holdCodeDesc'),
      this.translation.translate('orders.sortLabels.dateRequestAsc'),
      this.translation.translate('orders.sortLabels.dateRequestDesc'),
    ]).pipe(
      map(
        ([
          textOrderDateAsc,
          textOrderDateDesc,
          textDeliveryDateAsc,
          textDeliveryDateDesc,
          textOrderNumberAsc,
          textOrderNumberDesc,
          textPoNumberAsc,
          textPoNumberDesc,
          textOrderType,
          textItemNumberAsc,
          textItemNumberDesc,
          textHoldCodeNumberAsc,
          textHoldCodeNumberDesc,
          textDateRequestAsc,
          textDateRequestDesc,
        ]) => {
          return {
            orderdateasc: textOrderDateAsc,
            orderdatedesc: textOrderDateDesc,
            deliverydateasc: textDeliveryDateAsc,
            deliverydatedesc: textDeliveryDateDesc,
            ordernumberasc: textOrderNumberAsc,
            ordernumberdesc: textOrderNumberDesc,
            ponumberasc: textPoNumberAsc,
            ponumberdesc: textPoNumberDesc,
            ordertype: textOrderType,
            itemnumberasc: textItemNumberAsc,
            itemnumberdesc: textItemNumberDesc,
            holdcodeasc: textHoldCodeNumberAsc,
            holdcodedesc: textHoldCodeNumberDesc,
            daterequestasc: textDateRequestAsc,
            daterequestdesc: textDateRequestDesc,
          };
        }
      )
    );
  }

  getFeatures(): Observable<any> {
    return this.http.get(`../../../public/features/ORDER_HISTORY_FEATURES.json`).pipe(
      filter(Boolean),
      take(1),
      switchMap((features: any) =>
        combineLatest([of(features), this.countryService.getActive(), this.getOrderHistoryBaseSiteConfigs$()])
      ),
      map(([features, activeCountry, baseSiteConfigs]) => {
        return {
          ...(features[activeCountry] ? features[activeCountry] : features['default']),
          ...baseSiteConfigs,
        };
      }),
      catchError((error: any) => throwError(error))
    );
  }

  downloadOpenOrders(): Observable<any> {
    const options: DownloadOptions = {
      responseType: 'arraybuffer',
    };
    return this.shimanoFileDownloadService.downloadCsv(
      'GET',
      `${this.url}/users/${this.userCurrent}/orders/download`,
      options,
      'OPEN_ORDERS'
    );
  }

  exportCsv(payload: any, endpoint: string, fileName: string): Observable<any> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('fields', 'DEFAULT');

    const body: any = {
      search: payload.filterSearchData,
      searchBy: payload.filterSearchData === '' ? '' : payload.filterSearchBy,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
      status: payload.orderStatus,
      orderType: payload.orderTypes,
      currentPage: '',
      pageSize: '',
      sort: payload.sort,
      dir: payload.dir,
      timeZone: this.getTimeZoneId(),
    };

    const options: DownloadOptions = {
      responseType: 'arraybuffer',
      params: httpParams,
    };

    return this.shimanoFileDownloadService.downloadCsv(
      'POST',
      `${this.url}/users/${this.userCurrent}/${endpoint}`,
      options,
      fileName,
      body
    );
  }

  getactiveCartId() {
    let activeCartId: string;
    this.activeCart$.pipe(take(1)).subscribe((activeCart) => (activeCartId = activeCart));
    return activeCartId;
  }

  getActiveCartId() {
    const getActiveCart = this.cartService.takeActiveCartId();
    const getPreviousCart = this.cartService.requireLoadedCart();

    return combineLatest([getActiveCart, getPreviousCart]).pipe(
      take(1),
      retry(1),
      switchMap(([activeCartId, previousCart]) => {
        // Check cartId if exists
        if (!activeCartId) {
          // Check previous cart exist
          if (!previousCart.code) {
            // Create a cart if previous cart does not exist
            return this.multiCartService
              .createCart({
                userId: this.userCurrent,
                extraData: { active: true },
              })
              .pipe(
                take(1),
                map((newCart) => {
                  return newCart.code; // return new cartId
                })
              );
          }
          return of(previousCart.code); // return previous cartId
        }
        return of(activeCartId); // return existing cartId
      })
    );
  }

  getInventory(payload: any): Observable<any> {
    return this.getActiveCartId().pipe(
      take(1),
      switchMap((cartId) => {
        // Use the returned cartId from getActiveCartId method
        return this.http.post(
          `${this.url}/users/${this.userCurrent}/carts/${cartId}/inventory`,
          JSON.stringify(payload),
          { headers: this.headers }
        );
      }),
      map((data) => data)
    );
  }

  getInvoices(payload: GetInvoicesPayload): Observable<any> {
    const body = {
      search: payload.filterSearchData,
      searchBy: payload.filterSearchData === '' ? '' : payload.filterSearchBy,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
      status: payload.orderStatus,
      orderType: payload.orderTypes,
      currentPage: payload.currentPage,
      pageSize: payload.pageSize,
      sort: payload.sort,
      dir: payload.dir,
    };

    const params = new HttpParams().set('fields', payload.fields);

    this.setOrderHistoryFilterObservable({
      type: 'invoice',
      orderStatus: payload.orderStatus,
      orderType: payload.orderTypes,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
    });

    return this.http
      .post(`${this.url}/users/${this.userCurrent}/getInvoices`, JSON.stringify(body), {
        params,
        headers: this.headers,
      })
      .pipe(map((data) => data));
  }

  getInvoiceSortLabels(): Observable<{
    invoicedatedesc: string;
    invoicedateasc: string;
    duedatedesc: string;
    duedateasc: string;
    ponumberdesc: string;
    ponumberasc: string;
    ordertype: string;
    paymentstatus: string;
    daysoverduedesc: string;
  }> {
    return combineLatest([
      this.translation.translate('orders.sortLabels.invoiceDateDesc'),
      this.translation.translate('orders.sortLabels.invoiceDateAsc'),
      this.translation.translate('orders.sortLabels.dueDateDesc'),
      this.translation.translate('orders.sortLabels.dueDateAsc'),
      this.translation.translate('orders.sortLabels.invoicePONumberDesc'),
      this.translation.translate('orders.sortLabels.invoicePONumberAsc'),
      this.translation.translate('orders.sortLabels.invoiceOrderType'),
      this.translation.translate('orders.sortLabels.paymentStatus'),
      this.translation.translate('orders.sortLabels.daysOverdueDesc'),
    ]).pipe(
      map(
        ([
          textInvoiceDateDesc,
          textInvoiceDateAsc,
          textDueDateDesc,
          textDueDateAsc,
          textPONumberDesc,
          textPONumberAsc,
          textOrderType,
          textPaymentStatus,
          textDaysOverdueDesc,
        ]) => {
          return {
            invoicedatedesc: textInvoiceDateDesc,
            invoicedateasc: textInvoiceDateAsc,
            duedatedesc: textDueDateDesc,
            duedateasc: textDueDateAsc,
            ponumberdesc: textPONumberDesc,
            ponumberasc: textPONumberAsc,
            ordertype: textOrderType,
            paymentstatus: textPaymentStatus,
            daysoverduedesc: textDaysOverdueDesc,
          };
        }
      )
    );
  }

  getOrderHistoryBaseSiteConfigs$(): Observable<any> {
    const keys = Object.values(ORDER_HISTORY_BASESITE_CONFIG_KEYS);

    return this.baseSiteConfigService.getBaseSiteConfigValues$(keys).pipe(
      map((data: any) => {
        const configs: any = {};

        data.forEach((item: any) => {
          Object.entries(ORDER_HISTORY_BASESITE_CONFIG_KEYS).forEach(([propertyName, configKey]) => {
            if (item.key === configKey) {
              configs[propertyName] = item.value === 'true';
            }
          });
        });

        return configs;
      })
    );
  }

  getTimeZoneId(): string {
    var timeZoneId = moment.tz.guess();
    return timeZoneId;
  }

  getOrderSearchHideItemNumber$(): Observable<any> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.order.search.hide.item.number'])
      .pipe(map((configs) => configs && configs.map((res: any) => res.value === 'true')[0]));
  }
}
